import { themeTypography, themeFeatures, themeSize, mainGraphColorNames, commonColors } from '../utils/constants'
import { darkColors } from '../utils/darkPalette'
import { lightColors } from '../utils/lightPalette'
import { createTheme } from '@mui/material/styles'

const cssBaselineRoot = (dict) => {
  const colors = {}
  Object.keys(dict).forEach((key) => {
    if (typeof dict[key] === 'string') {
      colors[`--${key}`] = dict[key]
    }
  })
  return colors
}

const paletteColors = (arr) => {
  const colors = {}
  arr.forEach((key) => {
    colors[key]={main: commonColors[key]}
  })
  return colors
}

export const lightTheme = () => {
  const theme = commonTheme(lightColors)
  return createTheme({
    ...theme,
    palette: {
      type: 'light',
      ...theme.palette,
    },
    components: {
      ...theme.components,
      MuiSkeleton: {
        styleOverrides: {
          root: {
            backgroundColor: lightColors.dark5,
          }
        }
      },
      MuiButton: {
        ...theme.components.MuiButton,
        styleOverrides: {
          ...theme.components.MuiButton.styleOverrides,
          containedSecondary: {
            backgroundColor: 'inherit',
            color: lightColors.textPrimary,
            ...lightColors.paperBorder,
            '&:hover': {
              backgroundColor: `rgba(0,0,0,0.02)`,
              '@media (hover: none)': {
                backgroundColor: `rgba(0,0,0,0.02)`,
              },
            },
          },
        }
      },
      MuiPaper:{
        ...theme.components.MuiPaper,
        styleOverrides: {
          ...theme.components.MuiPaper.styleOverrides,
          elevation1: {
            ...theme.components.MuiPaper.styleOverrides.elevation1,
            backgroundColor: lightColors.dark1
          },
          elevation2: {
            ...theme.components.MuiPaper.styleOverrides.elevation2,
            ...lightColors.paperBorder
          },
          elevation7: {
            ...lightColors.paperBorder,
          },
        }
      }
    }
  })
}

export const darkTheme = () => {
  const theme = commonTheme(darkColors)
  return createTheme({
    ...theme,
    palette: {
      type: 'dark',
      ...theme.palette,
    },
  })
}

const commonTheme = (colors) => ({
  palette: {
    background: {
      default: colors.dark1,
      paper: colors.dark1
    },
    primary: {
      main: colors.ctaPrimary
    },
    secondary: {
      main: colors.ctaSecondary
    },
    success: {
      main: colors.stockUp
    },
    error: {
      main: colors.stockDown
    },
    text: {
      primary: colors.textPrimary,
      secondary: colors.textSecondary,
    },
    textPrimary:{
      main:colors.textPrimary,
    },
    disabled: {
      main: colors.disabled
    },
    icon: {
      main: colors.icon
    },
    textSecondary:{
      main:colors.textSecondary,
    },
    watchList:{
      main:colors.starColor,
    },
    active: {
      main:colors.stockUp
    },
    data0: {
      main: colors.data0
    }, ...paletteColors(mainGraphColorNames)
  },
  shape: {
    borderRadius: themeFeatures.borderRadius,
  },
  props: {
    MuiButton: {
      disableElevation: true,
    },
    MuiFilledInput: {
      disableUnderline: true,
    }
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  typography: {
    fontWeight: 300,
    fontFamily: "'Inter', sans-serif",
    fontWeightRegular: themeTypography.body.fontWeight,
    body1: {
      ...themeTypography.body
    },
    body2: {
      ...themeTypography.micro
    },
    h1: {
      ...themeTypography.h1
    },
    h2: {
      ...themeTypography.h2
    },
    h3: {
      ...themeTypography.h3
    },
    h4: {
      ...themeTypography.h4
    },
    h6: {
      ...themeTypography.h6
    }
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        elevation0: {
          backgroundColor: colors.dark4,
          boxShadow: 'none',
        },
        elevation1: {
          backgroundColor: colors.dark3,
          ...colors.paperBorder,
          transition: 'all 300ms',
        },
        elevation2: {
          backgroundColor: colors.dark2,
          boxShadow: 'none',
        },
        elevation3: {
          backgroundColor: colors.dark1,
          ...colors.paperBorder,
        },
        elevation5: {
          backgroundColor: colors.dark4,
          ...colors.paperBorder,
        },
        elevation6: { //active ElevationButton
          backgroundColor: colors.activeButton,
          ...colors.paperBorder,
        },
        elevation7: {
          backgroundColor: colors.dark1,
          boxShadow: 'none',
        },
        elevation8: {
          border: 'none',
        },
        elevation9: {
          backgroundColor: colors.dark6,
          boxShadow: 'none',
        },
        elevation10: {
          backgroundColor: 'transparent',
          boxShadow: 'none'
        },
        elevation12: {
          backgroundColor: colors.darkMain,
          boxShadow: 'none',
          border: `1px solid ${colors.textPrimary}`,
        },
      }
    },
    MuiTextField: {
      styleOverrides:{
        root: {
          '& label.Mui-focused': {
            color: colors.textPrimary,
          },
          '& .MuiOutlinedInput-root': {
            webkitAutofill: {
                webkitBoxShadow: '0 0 0 100px black inset' 
            },
            '&.Mui-focused fieldset': {
              borderColor: colors.textPrimary,
            },
          },
        }
      }
    },
    MuiOutlinedInput:{
      styleOverrides:{
        root: {
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.dark5
          },
        },
        input:{
          '&:-webkit-autofill': {
            WebkitBoxShadow :  `0 0 1000px ${colors.dark3} inset`,
            WebkitTextFillColor: colors.textPrimary
          }
        },
        notchedOutline: {
          borderColor: colors.dark5
        }
      }
    },
    MuiBackdrop:{
      styleOverrides: {
        root:{
          backgroundColor: colors.backdrop,
          backdropFilter: "blur(6px)"
        },
        invisible: {
          backgroundColor: "unset",
          backdropFilter: "unset"
        }
      }
    },
    MuiTable:{
      styleOverrides: {
        root:{
          tableLayout: 'fixed'
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `solid 1px ${colors.dark5}`,
          padding: '10px 5px',
        },
        head: {
          lineHeight: 'normal'
        }
      }
    },
    MuiChip: {
      styleOverrides:{
        root: {
          backgroundColor: colors.dark4,
          color: colors.textPrimary,
        }
      }
    },
    MuiLink: {
      styleOverrides:{
        root: {
          cursor: 'pointer'
        }
      }
    },
    MuiTooltip : {
      styleOverrides:{
        tooltip: {
          backgroundColor: `${colors.tooltip}`,
          fontSize: 13,
        },
        tooltipPlacementBottom:{
          '@media (min-width: 600px)': {
            margin: '0',
          },
        }
      }
    },
    MuiInputBase: {
      styleOverrides:{
        root: {
          '&.MuiFilledInput-root': {
            backgroundColor: 'unset'
          }
        }
      }
    },
    MuiTabs: {
      styleOverrides:{
        flexContainer: {
          height: '100%', // between Tabs and Tab, used in Header
        },
        vertical: {
          '& .MuiTab-wrapper': {
            justifyContent: 'flex-start',
          }
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper:{
          backgroundColor: colors.dark1,
        }
      }
    },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          backgroundColor: 'unset'
        }
      }
    },
    MuiTab: {
      styleOverrides:{
          root: {
            textTransform: 'none',
          },
          labelIcon: {
            minHeight: 'unset',
            '& .MuiTab-wrapper': {
              '&>*:first-child': {
                marginBottom: 0,
                marginRight: '6px',
              },
          }
        },
        wrapper: {
          flexDirection: 'row', // changes position of icon in tab
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root:{
          backgroundColor: `${colors.divider}`,
        }
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        ":root": {
          ...cssBaselineRoot(colors),
        },
        color: colors.textPrimary,
        body: {
          ...themeTypography.body,
        },
        fontFamily: "'Inter', sans-serif",
      },
    },
    MuiButton: {
      defaultProps: {
        color:'inherit'
      },
      styleOverrides: {
        outlined:{
          border: `1px solid ${colors.dark5}`,
          fontSize: '13px'
        },
        outlinedPrimary:{
          border: `1px solid ${colors.dark5}`,
          fontSize: '13px',
          color: 'unset',
        },
        root: {
          boxShadow: 'unset',
          textTransform: 'none',
          ':disabled': {
            color: 'rgba(255, 255, 255, 0.6)',
            backgroundColor: colors.disabled,
          }
        },
        containedSecondary: {
          backgroundColor: colors.dark1,
          color: colors.textPrimary,
          '&:hover': {
            backgroundColor: `${colors.dark1}`,
            '@media (hover: none)': {
              backgroundColor: `${colors.dark1}`,
            },
          },
        },
        sizeSmall: {
          minWidth: 'auto',
          height: themeSize.small
        },
      }
    },
    MuiCalendarPicker: {
      styleOverrides: {
        root: {
          '& > div': {
            backgroundColor: 'unset',
            '&:first-of-type .MuiButtonBase-root': {
              color: colors.textPrimary,
              '&: disabled' : {
                color:'unset',
                opacity : 0.6,
              },
            },
          },
          color: colors.textPrimary,
          '.MuiTypography-root': {
            color: 'unset'
          },
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          color: colors.textPrimary,
          backgroundColor: 'unset',
          '&:not(.Mui-selected)': {
            border: 'unset',
          },
        },
      }
    },
    MuiDataGrid: {
      styleOverrides: {
        columnHeaders: {
          borderBottom: `solid 1px ${colors.dark5}`,
        },
        cell: {
          borderBottom: `solid 1px ${colors.dark5}`
        },
        columnsContainer: {
          borderBottom: `solid 1px ${colors.dark5}`
        },
        iconSeparator: {
          display: "none"
        },
        root: {
          border: `solid 1px ${colors.dark5}`,
          '& .MuiButtonBase-root.MuiIconButton-root': {
            color: 'unset',
            '&:disabled': {
              opacity: 0.6,
            }
          }
        },
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: colors.textPrimary
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: colors.textPrimary,
          ':disabled': {
            color: colors.disabled,
          },
        }
      }
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: colors.dark2,
        }
      }
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          backgroundColor: 'none',
          borderRadius: themeFeatures.borderRadius
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          minHeight: 'unset',
        },
        icon: {
          color: 'unset',
        }
      }
    },
    MuiSvgIcon : {
      styleOverrides: {
        fontSizeSmall : {
          fontSize: 17,
        }
      }
    },
    MuiStepper : {
      styleOverrides: {
        root: {
          backgroundColor: 'unset',
        }
      }
    },
    MuiStepLabel : {
      styleOverrides: {
        iconContainer: {
          width: 24,
          height: 24,
          borderRadius: 50,
        },
        label: {
          fontWeight: "bold",
          fontSize: 14,
          color: colors.paragraphColor,
          '&.MuiStepLabel-active':  {
            color: colors.stockUp,
            fontWeight: "bold",
          },
          '&.MuiStepLabel-completed':  {
            color: colors.paragraphColor,
            fontWeight: "bold",
          }
        }
      }
    },
    MuiMenu : {
      styleOverrides: {
        list : {
          padding: 0,
        }
      }
    },
    MuiFormLabel : {
      styleOverrides: {
        root : {
          "&.Mui-focused" : {
            color: colors.textPrimary
          },
        }
      }
    }
  }
})
